import { parseDate } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Tables } from "@/gen/tableNames/tables";
import i18n from "@/i18n";
import { ExternalLink } from "@/components/ExternalLink";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { formatNumber } from "@/features/Table/utils";
import { Link } from "@heroui/link";
import { createEnum } from "@/lib/utils/createEnum";

export type E2DynamicData = {
  id: number;
  date?: string | null;
  strategy_id: number;
  strategy_name: number;
  portfolio_id?: number | null;
  portfolio_name?: string | null;
  credit_quality_step?: number | null;
  book_value?: number | null;
  created_at: string;
  updated_at: string;
};

export const e2DynamicDataFields = createEnum<E2DynamicData>();

const t = (key: string, defaultValue: string) =>
  i18n.t(`${Tables.E2_DYNAMIC_DATA}.columns.${key}`, {
    ns: "tables",
    defaultValue,
  });

export const e2_dynamic_data_columns: ColumnDef<E2DynamicData>[] = [
  {
    accessorKey: e2DynamicDataFields.id,
    header: () => t(e2DynamicDataFields.id, "ID"),
  },
  {
    accessorKey: e2DynamicDataFields.date,
    header: () => t(e2DynamicDataFields.date, "Date"),
    cell: ({ row }) => {
      const value = row.getValue(e2DynamicDataFields.date);
      return value ? parseDate(String(value)) : "";
    },
  },
  {
    accessorKey: e2DynamicDataFields.strategy_name,
    header: () => t(e2DynamicDataFields.strategy_name, "Stratégie"),
    cell: ({ row }) => {
      const id = row.original[e2DynamicDataFields.strategy_id];
      const name = row.original[e2DynamicDataFields.strategy_name];
      if (!id || !name) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: e2DynamicDataFields.portfolio_name,
    header: () => t(e2DynamicDataFields.portfolio_name, "Portefeuille"),
    cell: ({ row }) => {
      const id = row.original[e2DynamicDataFields.portfolio_id];
      const name = row.original[e2DynamicDataFields.portfolio_name];
      if (!id || !name) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: e2DynamicDataFields.credit_quality_step,
    header: () => (
      <div className="text-right">
        {t(e2DynamicDataFields.credit_quality_step, "Credit quality step")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(e2DynamicDataFields.credit_quality_step))}
      </div>
    ),
  },
  {
    accessorKey: e2DynamicDataFields.book_value,
    header: () => (
      <div className="text-right">
        {t(e2DynamicDataFields.book_value, "Book value")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(e2DynamicDataFields.book_value))}
      </div>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(e2DynamicDataFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
